/* eslint-disable @typescript-eslint/naming-convention */
import { Size } from '@ps/web-core-enums';
import type { GeoRule, IconsGrid } from '@ps/web-martech-cms-types';
import type { ItemBlock } from '@ps/web-martech-cms-types/IconsGrid';
import { DismissIcon, InfoIcon, useScreenSize } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useRef, useState } from 'react';

import { CMSHTML, CMSImage } from '@swf/cms';
import useOnClickOutside from '@swf/core/hooks/useOnClickOutside';

import useGeoVisibility from '../CMSContainer/hooks/useGeoVisibility';

import styles from './CMSIconsGrid.scss';

type Props = IconsGrid & {
  items: ItemBlock[];
  visibilityRules: GeoRule[];
  wrapperClassName?: string;
};

const IconsGridItems: React.FC<Props> = ({
  items,
  visibilityRules,
  icon_alignment,
  number_of_columns,
  number_of_columns_on_mobile,
  with_border,
  wrapperClassName
}) => {
  const { greaterThan } = useScreenSize();
  const [currentTooltip, setCurrentTooltip] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isMobile = !greaterThan(Size.Small);
  const [isVisible] = useGeoVisibility(visibilityRules || []);

  useOnClickOutside(containerRef, () => setCurrentTooltip(null));

  if (!isVisible || !items) return null;

  return (
    <div
      className={classNames(
        styles.grid,
        styles[`col${number_of_columns}` as keyof typeof styles],
        isMobile && styles[`mobileCol${number_of_columns_on_mobile}` as keyof typeof styles]
      )}
    >
      {items.map(({ icon, text, tooltip }, index) => {
        const hasPredefinedDimensions = icon.width && icon.height;
        const bigIcon = icon.width && icon.width > 50;

        return (
          <div key={index} className={classNames(styles.wrapper, wrapperClassName)}>
            <div
              onClick={() => isMobile && setCurrentTooltip(index)}
              className={classNames(
                styles.gridItem,
                with_border && styles[with_border.toLowerCase() as keyof typeof styles]
              )}
            >
              {isMobile && tooltip?.content && (
                <InfoIcon width={24} height={24} className={styles.infoIcon} />
              )}
              <div
                className={classNames(
                  styles.icon,
                  icon_alignment && styles[icon_alignment as keyof typeof styles],
                  {
                    [styles.dimensions]: !hasPredefinedDimensions
                  }
                )}
              >
                <CMSImage {...icon} />
              </div>
              <div className={classNames(styles.text, bigIcon && styles.bigIcon)}>
                <CMSHTML {...text} />
                {!isMobile && tooltip?.content && <CMSHTML {...tooltip} />}
              </div>
            </div>
            {isMobile && tooltip?.content && currentTooltip === index && (
              <div className={styles.tooltip} ref={containerRef}>
                <CMSHTML {...text} />
                <CMSHTML {...tooltip} />
                <button
                  type="button"
                  onClick={() => setCurrentTooltip(null)}
                  className={styles.close}
                >
                  <DismissIcon width={18} height={18} />
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default IconsGridItems;
